"use client";

import { useEffect, useMemo, useRef } from "react";
import {
  useMarketDataRegistry,
  type RegistryMarketDataSymbol,
  type MarketDataTick,
  type MarketDataGreeks,
  type MarketDataExpectedMove,
  type MarketDataStreamStatus,
} from "@/contexts/market-data-registry-context";

export type {
  MarketDataTick,
  MarketDataGreeks,
  MarketDataExpectedMove,
  MarketDataStreamStatus,
} from "@/contexts/market-data-registry-context";

export interface MarketDataStreamsOptions {
  includeGreeks?: boolean;
  includeExpectedMove?: boolean;
}

interface StreamState {
  ticks: Record<string, MarketDataTick>;
  greeks: Record<string, MarketDataGreeks>;
  expectedMoves: Record<string, MarketDataExpectedMove>;
  status: Record<string, MarketDataStreamStatus>;
  errors: Record<string, string | null>;
}

let streamInstanceCounter = 0;

const toUpperSymbol = (value: string): string => value.trim().toUpperCase();

const addSymbolAliases = (set: Set<string>, value: string): void => {
  const trimmed = value.trim();
  if (!trimmed) {
    return;
  }

  const upper = trimmed.toUpperCase();
  set.add(upper);

  const compact = upper.replace(/\s+/g, "");
  set.add(compact);

  if (compact.startsWith(".")) {
    set.add(compact.slice(1));
  } else {
    set.add(`.${compact}`);
  }
};

const createRequestSymbols = (
  symbols: ReadonlyArray<string>,
): RegistryMarketDataSymbol[] =>
  symbols
    .map((symbol) => symbol?.trim())
    .filter((value): value is string => !!value)
    .map((value) => ({ symbol: toUpperSymbol(value) }));

const filterRecordBySymbols = <T,>(
  source: Map<string, T>,
  allowed: Set<string>,
): Record<string, T> => {
  const next: Record<string, T> = {};
  if (allowed.size === 0 || source.size === 0) {
    return next;
  }
  source.forEach((value, key) => {
    if (allowed.has(key)) {
      next[key] = value;
    }
  });
  return next;
};

export function useMarketDataStreams(
  symbols: ReadonlyArray<string>,
  options: MarketDataStreamsOptions = {},
): StreamState {
  const includeGreeks = options.includeGreeks ?? false;
  const includeExpectedMove = options.includeExpectedMove ?? false;

  // Serialize the requested symbols so identity stays stable across renders
  // even when callers pass a freshly-constructed array with identical content.
  const symbolsKey = useMemo(() => {
    const set = new Set<string>();
    symbols.forEach((value) => {
      if (!value) return;
      const trimmed = value.trim();
      if (!trimmed) return;
      set.add(trimmed.toUpperCase());
    });
    return Array.from(set).join(",");
  }, [symbols]);

  const normalizedSymbols = useMemo(
    () => (symbolsKey ? symbolsKey.split(",") : []),
    [symbolsKey],
  );

  const { registerSource, unregisterSource, ticks, greeks, expectedMoves, status, errors } =
    useMarketDataRegistry();

  const sourceKeyRef = useRef<string>("");
  if (!sourceKeyRef.current) {
    streamInstanceCounter += 1;
    sourceKeyRef.current = `useMarketDataStreams:${streamInstanceCounter}`;
  }

  useEffect(() => {
    const sourceKey = sourceKeyRef.current;
    if (normalizedSymbols.length === 0) {
      registerSource(sourceKey, { symbols: [], includeGreeks, includeExpectedMove });
      return () => {
        unregisterSource(sourceKey);
      };
    }

    const requestSymbols = createRequestSymbols(normalizedSymbols);
    registerSource(sourceKey, {
      symbols: requestSymbols,
      includeGreeks,
      includeExpectedMove,
    });

    return () => {
      unregisterSource(sourceKey);
    };
  }, [
    includeGreeks,
    includeExpectedMove,
    normalizedSymbols,
    registerSource,
    unregisterSource,
  ]);

  const allowedSymbols = useMemo(
    () => {
      const set = new Set<string>();
      normalizedSymbols.forEach((symbol) => addSymbolAliases(set, symbol));
      return set;
    },
    [normalizedSymbols],
  );

  // Memoize each filtered record on the source map identity + the allowed
  // symbols so consumers get a stable object identity unless underlying data
  // actually changed.
  const filteredTicks = useMemo(
    () => filterRecordBySymbols(ticks, allowedSymbols),
    [ticks, allowedSymbols],
  );
  const filteredGreeks = useMemo(
    () => filterRecordBySymbols(greeks, allowedSymbols),
    [greeks, allowedSymbols],
  );
  const filteredExpectedMoves = useMemo(
    () => filterRecordBySymbols(expectedMoves, allowedSymbols),
    [expectedMoves, allowedSymbols],
  );
  const filteredStatus = useMemo(
    () =>
      filterRecordBySymbols(status, allowedSymbols) as Record<
        string,
        MarketDataStreamStatus
      >,
    [status, allowedSymbols],
  );
  const filteredErrors = useMemo(
    () => filterRecordBySymbols(errors, allowedSymbols),
    [errors, allowedSymbols],
  );

  return {
    ticks: filteredTicks,
    greeks: filteredGreeks,
    expectedMoves: filteredExpectedMoves,
    status: filteredStatus,
    errors: filteredErrors,
  };
}
